import { useMemo } from 'react';
import { useJobs } from '../../jobs/hooks/useJobs';
import { useCustomers } from '../../customers/hooks/useCustomers';
import { useQuotes } from '../../quotes/hooks/useQuotes';
import { useRequests } from '../../requests/hooks/useRequests';
// import { useInvoices } from '../../invoices/hooks/useInvoices';
import {
  REQUEST_STATUS,
  QUOTE_STATUS,
  JOB_STATUS,
  type WorkflowMetrics,
  type TimeBasedMetrics,
  type WorkflowAlert,
  type WorkflowTrends,
} from '@cleanpro/common-ui';
import { 
  isToday, 
  isThisWeek, 
  isThisMonth, 
  startOfMonth, 
  endOfMonth, 
  subMonths, 
  format
} from 'date-fns';

export function useWorkflowMetrics() {
  const { jobs } = useJobs();
  const { customers } = useCustomers();
  const { requests } = useRequests();
  const { quotes } = useQuotes();

  // Calculate workflow metrics
  const workflowMetrics = useMemo((): WorkflowMetrics => {
    if (!jobs || !requests || !quotes) {
      const emptyRequestMetrics = {
        total: 0,
        new: 0,
        pending: 0,
        assessed: 0,
        quoted: 0,
        expired: 0,
        cancelled: 0,
        assessmentComplete: 0,
        overdue: 0,
        avgResponseTime: 0,
        conversionRate: 0
      };
      const emptyQuoteMetrics = {
        total: 0,
        requireAction: 0,
        draft: 0,
        awaitingResponse: 0,
        pendingAssignment: 0,
        approved: 0,
        changesRequested: 0,
        archived: 0,
        avgValue: 0,
        acceptanceRate: 0
      };
      const emptyJobMetrics = {
        total: 0,
        upcomingJobs: 0,
        requested: 0,
        confirmed: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0,
        enRoute: 0,
        invoiced: 0,
        requiresInvoicing: 0,
        overdue: 0,
        avgDuration: 0,
        completionRate: 0
      };
      return {
        requests: emptyRequestMetrics,
        quotes: emptyQuoteMetrics,
        jobs: emptyJobMetrics
      };
    }
    const now = new Date();
    
    // Request metrics (using actual requests table)
    const overdueRequests = requests.filter(request => {
      if (request.status === REQUEST_STATUS.QUOTED || 
          request.status === REQUEST_STATUS.EXPIRED || 
          request.status === REQUEST_STATUS.CANCELLED) return false;
      const requestDate = new Date(request.createdAt);
      // Consider request overdue if older than 24 hours without quote
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return requestDate < oneDayAgo && !quotes.some(q => q.requestId === request.id);
    });

    // Total, Assessed (76), Pending (60), Quoted (62), Expired (68), Cancelled (84)
    const requestMetrics = {
      total: requests.length,
      new: requests.filter(req => req.status === REQUEST_STATUS.PENDING).length, // New requests are pending ones
      pending: requests.filter(req => req.status === REQUEST_STATUS.PENDING).length,
      assessed: requests.filter(req => req.status === REQUEST_STATUS.ASSESSED).length,
      quoted: requests.filter(req => req.status === REQUEST_STATUS.QUOTED).length,
      expired: requests.filter(req => req.status === REQUEST_STATUS.EXPIRED).length,
      cancelled: requests.filter(req => req.status === REQUEST_STATUS.CANCELLED).length,
      assessmentComplete: requests.filter(req => 
        quotes.some(q => q.requestId === req.id)
      ).length,
      overdue: overdueRequests.length,
      avgResponseTime: 0, // Would need more detailed timestamps
      conversionRate: requests.length > 0 ? 
        (quotes.length / requests.length) * 100 : 0
    };

    // Quote metrics
    const approvedQuotes = quotes.filter(quote => quote.status === QUOTE_STATUS.APPROVED);
    const quoteMetrics = {
      total: quotes.length,
      requireAction:
        quotes.filter(quote => quote.status === QUOTE_STATUS.DRAFT).length
        + quotes.filter(quote => quote.status === QUOTE_STATUS.AWAITING_RESPONSE).length
        + quotes.filter(quote => quote.status === QUOTE_STATUS.CHANGES_REQUESTED).length
        + quotes.filter(quote => quote.status === QUOTE_STATUS.APPROVED).length
        + quotes.filter(quote => quote.status === QUOTE_STATUS.PENDING_ASSIGNMENT).length,
      draft: quotes.filter(quote => quote.status === QUOTE_STATUS.DRAFT).length,
      awaitingResponse: quotes.filter(quote => quote.status === QUOTE_STATUS.AWAITING_RESPONSE).length,
      approved: quotes.filter(quote => quote.status === QUOTE_STATUS.APPROVED).length,
      pendingAssignment: quotes.filter(quote => quote.status === QUOTE_STATUS.PENDING_ASSIGNMENT).length,
      changesRequested: quotes.filter(quote => quote.status === QUOTE_STATUS.CHANGES_REQUESTED).length,
      archived: quotes.filter(quote => quote.status === QUOTE_STATUS.ARCHIVED).length,
      avgValue: quotes.length > 0 ? quotes.reduce((sum, quote) => sum + quote.totalAmount, 0) / quotes.length : 0,
      acceptanceRate: quotes.length > 0 ? (approvedQuotes.length / quotes.length) * 100 : 0
    };

    // Job metrics
    const upcomingJobs = jobs.filter(
      job => job.status !== JOB_STATUS.CANCELLED &&
      job.status !== JOB_STATUS.COMPLETED &&
      job.status !== JOB_STATUS.INVOICED
    );
    
    const jobMetrics = {
      upcomingJobs: upcomingJobs.length,
      total: jobs.length,
      requested: jobs.filter(job => job.status === JOB_STATUS.REQUESTED).length,
      confirmed: jobs.filter(job => job.status === JOB_STATUS.CONFIRMED).length,
      inProgress: jobs.filter(job => job.status === JOB_STATUS.IN_PROGRESS).length,
      completed: jobs.filter(job => job.status === JOB_STATUS.COMPLETED).length,
      cancelled: jobs.filter(job => job.status === JOB_STATUS.CANCELLED).length,
      enRoute: jobs.filter(job => job.status === JOB_STATUS.EN_ROUTE).length,
      invoiced: jobs.filter(job => job.status === JOB_STATUS.INVOICED).length,
      requiresInvoicing: 0,
      overdue: jobs.filter(job => {
        const jobDate = new Date(job.date_time);
        return jobDate < now && job.status !== JOB_STATUS.COMPLETED && job.status !== JOB_STATUS.CANCELLED;
      }).length,
      avgDuration: upcomingJobs.reduce((sum, job) => sum + job.duration, 0) / (upcomingJobs.length || 1),
      completionRate: 0
    };

    // Invoice metrics
    // const draftInvoices = invoices.filter(inv => inv.status === INVOICE_STATUS.DRAFT);
    // const sentInvoices = invoices.filter(inv => inv.status === INVOICE_STATUS.SENT);
    // const paidInvoices = invoices.filter(inv => inv.status === INVOICE_STATUS.PAID);
    // const overdueInvoices = invoices.filter(inv => {
    //   if (inv.status !== INVOICE_STATUS.SENT) return false;
    //   const dueDate = new Date(inv.dueDate);
    //   return dueDate < now;
    // });
    
    // const invoiceMetrics = {
    //   total: invoices.length,
    //   draft: draftInvoices.length,
    //   sent: sentInvoices.length,
    //   paid: paidInvoices.length,
    //   overdue: overdueInvoices.length,
    //   outstandingValue: invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0),
    //   totalValue: invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0),
    //   avgPaymentTime: 0, // Would need payment timestamps
    //   collectionRate: paidInvoices.length / (invoices.length || 1) * 100
    // };

    return {
      requests: requestMetrics,
      quotes: quoteMetrics,
      jobs: jobMetrics
    };
  }, [jobs, requests, quotes]);

  // Calculate time-based metrics
  const timeBasedMetrics = useMemo((): TimeBasedMetrics => {
    if (!jobs || !customers) {
      return {
        today: { appointmentsScheduled: 0, appointmentsCompleted: 0, revenue: 0, newCustomers: 0 },
        thisWeek: { jobsScheduled: 0, jobsCompleted: 0, revenue: 0, avgRating: 0 },
        thisMonth: { totalJobs: 0, totalRevenue: 0, newCustomers: 0, avgCustomerValue: 0, growthRate: 0 },
        quarter: { totalRevenue: 0, customerRetention: 0, avgJobValue: 0, profitMargin: 0 }
      };
    }
    const todayJobs = jobs.filter(job => isToday(new Date(job.date_time)));
    const thisWeekJobs = jobs.filter(job => isThisWeek(new Date(job.date_time)));
    const thisMonthJobs = jobs.filter(job => isThisMonth(new Date(job.date_time)));

    const thisMonthCustomers = customers.filter(customer => 
      customer.lastService && isThisMonth(new Date(customer.lastService))
    );

    return {
      today: {
        appointmentsScheduled: todayJobs.length,
        appointmentsCompleted: todayJobs.filter(job => job.status === JOB_STATUS.COMPLETED).length,
        revenue: 0,
        newCustomers: 0
      },
      thisWeek: {
        jobsScheduled: thisWeekJobs.length,
        jobsCompleted: thisWeekJobs.filter(job => job.status === JOB_STATUS.COMPLETED).length,
        revenue: 0,
        avgRating: 0
      },
      thisMonth: {
        totalJobs: thisMonthJobs.length,
        totalRevenue: 0,
        newCustomers: thisMonthCustomers.length,
        avgCustomerValue: 0,
        growthRate: 0
      },
      quarter: {
        totalRevenue: 0,
        customerRetention: 0,
        avgJobValue: thisMonthJobs.reduce((sum, job) => sum + job.price, 0) / (thisMonthJobs.length || 1),
        profitMargin: 0
      }
    };
  }, [jobs, customers]);

  // Generate workflow alerts
  const workflowAlerts = useMemo((): WorkflowAlert[] => {
    if (!jobs ) {
      return [];
    }
    const alerts: WorkflowAlert[] = [];
    const now = new Date();

    const overdueRequests = jobs.filter(job => {
      if (job.status !== JOB_STATUS.REQUESTED) return false;
      const jobDate = new Date(job.date_time);
      return jobDate < now;
    });

    if (overdueRequests.length > 0) {
      alerts.push({
        id: 'overdue-requests',
        type: 'overdue',
        title: `${overdueRequests.length} Overdue Requests`,
        description: 'These requests need immediate attention',
        actionUrl: '/jobs?filter=overdue',
        severity: 'high',
        createdAt: now.toISOString()
      });
    }

    return alerts;
  }, [jobs]);

  // Generate trend data for charts
  const workflowTrends = useMemo((): WorkflowTrends => {
    if (!jobs || !customers) {
      return { jobs: [], customers: [], satisfaction: [] };
    }
    const months = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const monthDate = subMonths(now, i);
      const monthStart = startOfMonth(monthDate);
      const monthEnd = endOfMonth(monthDate);
      
      const monthJobs = jobs.filter(job => {
        const jobDate = new Date(job.date_time);
        return jobDate >= monthStart && jobDate <= monthEnd;
      });
      
      const monthCustomers = customers.filter(customer => {
        if (!customer.lastService) return false;
        const serviceDate = new Date(customer.lastService);
        return serviceDate >= monthStart && serviceDate <= monthEnd;
      });

      months.push({
        period: format(monthDate, 'MMM yyyy'),
        jobs: monthJobs.length,
        customers: monthCustomers.length,
        satisfaction: 0
      });
    }

    return {
      jobs: months.map(m => ({ period: m.period, value: m.jobs })),
      customers: months.map(m => ({ period: m.period, value: m.customers })),
      satisfaction: months.map(m => ({ period: m.period, value: m.satisfaction }))
    };
  }, [jobs, customers]);

  // Calculate key performance indicators
  const kpis = useMemo(() => {
    if (!jobs || !customers) {
      return { totalRevenue: 0, avgJobValue: 0, totalReceivables: 0, activeCustomers: 0, jobCompletionRate: 0, avgCustomerSatisfaction: 0 };
    }
    
    return {
      activeCustomers: customers.length,
      jobCompletionRate: (jobs.filter(job => job.status === JOB_STATUS.COMPLETED).length / (jobs.length || 1)) * 100,
      avgCustomerSatisfaction: 0
    };
  }, [jobs, customers]);

  return {
    workflowMetrics,
    timeBasedMetrics,
    workflowAlerts,
    workflowTrends,
    kpis
  };
}
